import { GeneralLayout } from "@components";
import ErrorComponent from "@src/components/errors";
import FeatureBlock from "@src/modules/member/components/feature-block";
import NotFoundPage from "@src/modules/member/components/not-found";
import RoutePermissionGuard from "@src/modules/member/components/route-permission-guard";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { FeatureSlug } from "@src/modules/member/models/organization";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

import OrganizationContextProvider from "../../../context/organization-context";
import { ORG_CODE_PARAM } from "../../../path";
import { memberExtraMenuList, memberMainMenuList } from "./menu";
import {
  CLIENT_DETAILS_PATH,
  CLIENT_LIST_PATH,
  CONTRACT_DETAILS_PATH,
  CONTRACT_LIST_PATH,
  DASHBOARD_PATH,
  EXPENSES_LIST_PATH,
  FORMS_DETAILS_PATH,
  FORMS_LIST_PATH,
  FORM_NEW_FORM,
  INVOICE_DETAILS_PATH,
  INVOICE_LIST_PATH,
  PROJECT_DETAILS_PATH,
  PROJECT_LIST_PATH,
  RECURRING_INVOICE_DETAILS_PATH,
  RECURRING_INVOICE_LIST_PATH,
  RECURRING_INVOICE_NEW_FORM,
  SURCHARGE_DETAILS_PATH,
  SURCHARGE_LIST_PATH,
  TAXES_DETAILS_PATH,
  TAXES_LIST_PATH,
  TIME_ENTRIES_LIST_PATH,
  TRANSACTIONS_LIST_PATH,
} from "./paths";

const ClientDetails = lazy(
  () => import("@src/modules/member/modules/main/clients/details"),
);
const ClientList = lazy(
  () => import("@src/modules/member/modules/main/clients/list"),
);
const FormList = lazy(() => import("@src/modules/member/modules/main/forms"));
const InvoiceDetails = lazy(
  () => import("@src/modules/member/modules/main/invoices/invoices/details"),
);
const InvoicesModule = lazy(
  () => import("@src/modules/member/modules/main/invoices/invoices"),
);
const ContractsModule = lazy(
  () => import("@src/modules/member/modules/main/contracts/contracts"),
);
const ContractDetailShell = lazy(
  () => import("@src/modules/member/modules/main/contracts/contracts/details"),
);
const ContractDocumentTab = lazy(
  () =>
    import("@src/modules/member/modules/main/contracts/contracts/details/document"),
);
const ContractBillingTab = lazy(
  () =>
    import("@src/modules/member/modules/main/contracts/contracts/details/billing"),
);
const TransactionsList = lazy(
  () => import("@src/modules/member/modules/main/transactions/list"),
);
const SurchargeList = lazy(
  () => import("@src/modules/member/modules/main/fees/surcharges/list"),
);
const SurchargeDetails = lazy(
  () => import("@src/modules/member/modules/main/fees/surcharges/details"),
);
const TaxList = lazy(
  () => import("@src/modules/member/modules/main/fees/taxes"),
);
const TaxDetails = lazy(
  () => import("@src/modules/member/modules/main/fees/taxes/details"),
);
const SwitchOrganizationDialog = lazy(
  () =>
    import("@src/modules/member/modules/main/router/menu/switch-organization-dialog"),
);
const NewRecurringInvoice = lazy(
  () => import("@src/modules/member/modules/main/invoices/recurring/new"),
);
const RecurringInvoiceDetails = lazy(
  () => import("@src/modules/member/modules/main/invoices/recurring/details"),
);
const NewForm = lazy(
  () => import("@src/modules/member/modules/main/forms/new"),
);
const UpdateForm = lazy(
  () => import("@src/modules/member/modules/main/forms/update"),
);
const Dashboard = lazy(
  () => import("@src/modules/member/modules/main/dashboard"),
);
const TimeEntriesList = lazy(
  () => import("@src/modules/member/modules/main/time-entries"),
);
const ExpensesList = lazy(
  () => import("@src/modules/member/modules/main/expenses"),
);
const ProjectList = lazy(
  () => import("@src/modules/member/modules/main/projects/list"),
);
const ProjectDetails = lazy(
  () => import("@src/modules/member/modules/main/projects/details"),
);
const RecurringInvoicesList = lazy(
  () => import("@src/modules/member/modules/main/invoices/recurring"),
);

const protectedMemberRoutes = [
  {
    path: ORG_CODE_PARAM,
    element: (
      <OrganizationContextProvider>
        <SwitchOrganizationDialog />
        <GeneralLayout
          menuList={memberMainMenuList}
          extraMenu={memberExtraMenuList}
        />
      </OrganizationContextProvider>
    ),
    children: [
      {
        errorElement: <ErrorComponent />,
        children: [
          {
            path: "",
            element: <Navigate to={DASHBOARD_PATH()} />,
          },
          {
            path: DASHBOARD_PATH(),
            element: <Dashboard />,
          },
          {
            path: INVOICE_LIST_PATH(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.INVOICES_VIEW_LIST}>
                <InvoicesModule />
              </RoutePermissionGuard>
            ),
          },
          {
            path: INVOICE_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.INVOICES_VIEW_DETAILS}
              >
                <InvoiceDetails />
              </RoutePermissionGuard>
            ),
          },
          {
            path: CONTRACT_LIST_PATH(),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.CONTRACTS_VIEW_LIST}
              >
                <ContractsModule />
              </RoutePermissionGuard>
            ),
          },
          {
            path: CONTRACT_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.CONTRACTS_VIEW_DETAILS}
              >
                <ContractDetailShell />
              </RoutePermissionGuard>
            ),
            children: [
              { index: true, element: <ContractDocumentTab /> },
              { path: "billing", element: <ContractBillingTab /> },
            ],
          },
          {
            path: RECURRING_INVOICE_LIST_PATH(),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.RECURRING_INVOICES_VIEW_LIST}
              >
                <FeatureBlock feature={FeatureSlug.RecurringInvoices}>
                  <RecurringInvoicesList />
                </FeatureBlock>
              </RoutePermissionGuard>
            ),
          },
          {
            path: RECURRING_INVOICE_NEW_FORM(),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.RECURRING_INVOICES_CREATE}
              >
                <FeatureBlock feature={FeatureSlug.RecurringInvoices}>
                  <NewRecurringInvoice />
                </FeatureBlock>
              </RoutePermissionGuard>
            ),
          },
          {
            path: RECURRING_INVOICE_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.RECURRING_INVOICES_VIEW_DETAILS}
              >
                <FeatureBlock feature={FeatureSlug.RecurringInvoices}>
                  <RecurringInvoiceDetails />
                </FeatureBlock>
              </RoutePermissionGuard>
            ),
          },
          {
            path: TRANSACTIONS_LIST_PATH(),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.TRANSACTIONS_VIEW_LIST}
              >
                <TransactionsList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: SURCHARGE_LIST_PATH(),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.SURCHARGES_VIEW_LIST}
              >
                <SurchargeList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: SURCHARGE_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.SURCHARGES_VIEW_DETAILS}
              >
                <SurchargeDetails />
              </RoutePermissionGuard>
            ),
          },
          {
            path: TAXES_LIST_PATH(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.TAXES_VIEW_LIST}>
                <TaxList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: TAXES_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.TAXES_VIEW_DETAILS}>
                <TaxDetails />
              </RoutePermissionGuard>
            ),
          },
          {
            path: CLIENT_LIST_PATH(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.CLIENTS_VIEW_LIST}>
                <ClientList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: CLIENT_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.CLIENTS_VIEW_DETAILS}
              >
                <ClientDetails />
              </RoutePermissionGuard>
            ),
          },
          {
            path: TIME_ENTRIES_LIST_PATH(),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.TIME_ENTRIES_VIEW_LIST}
              >
                <TimeEntriesList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: EXPENSES_LIST_PATH(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.EXPENSES_VIEW}>
                <ExpensesList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: PROJECT_LIST_PATH(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.PROJECTS_VIEW_LIST}>
                <ProjectList />
              </RoutePermissionGuard>
            ),
          },
          {
            path: PROJECT_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard
                permission={PERMISSIONS.PROJECTS_VIEW_DETAILS}
              >
                <ProjectDetails />
              </RoutePermissionGuard>
            ),
          },
          {
            path: FORMS_LIST_PATH(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.FORMS_VIEW_LIST}>
                <FeatureBlock feature={FeatureSlug.Forms}>
                  <FormList />
                </FeatureBlock>
              </RoutePermissionGuard>
            ),
          },
          {
            path: FORM_NEW_FORM(),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.FORMS_CREATE}>
                <FeatureBlock feature={FeatureSlug.Forms}>
                  <NewForm />
                </FeatureBlock>
              </RoutePermissionGuard>
            ),
          },
          {
            path: FORMS_DETAILS_PATH(":code"),
            element: (
              <RoutePermissionGuard permission={PERMISSIONS.FORMS_VIEW_DETAILS}>
                <FeatureBlock feature={FeatureSlug.Forms}>
                  <UpdateForm />
                </FeatureBlock>
              </RoutePermissionGuard>
            ),
          },
          {
            path: "*",
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
];

export default protectedMemberRoutes;
